import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { NewBadge, isNew } from "@/components/NewBadge";
import { catsOf } from "@/lib/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `₹${n.toLocaleString("en-IN")}`;
const CATEGORIES = ["All", "Kurtis", "Anarkali", "Dresses", "Festive Wear"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

function applyFilters(products, { category, size, maxPrice, onlyNew }) {
  let list = products;
  if (onlyNew) list = list.filter(isNew);
  if (category !== "All") list = list.filter(p => catsOf(p).includes(category));
  if (size) list = list.filter(p => p.sizes?.includes(size));
  if (maxPrice) list = list.filter(p => p.price <= Number(maxPrice));
  return list;
}

function applySort(list, sort) {
  if (sort === "price-asc") return [...list].sort((a, b) => a.price - b.price);
  if (sort === "price-desc") return [...list].sort((a, b) => b.price - a.price);
  if (sort === "rating") return [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
  return list;
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "All";
  const size = params.get("size") || "";
  const maxPrice = params.get("max") || "";
  const sort = params.get("sort") || "featured";
  const onlyNew = params.get("new") === "1";

  useEffect(() => { axios.get(`${API}/products`).then(r => setProducts(r.data)).finally(() => setLoading(false)); }, []);

  const filtered = useMemo(() => applySort(applyFilters(products, { category, size, maxPrice, onlyNew }), sort), [products, category, size, maxPrice, onlyNew, sort]);

  const setFilter = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  return (
    <div className="shop-page" data-testid="shop-page">
      <header className="shop-hero">
        <span className="lux-eyebrow dark">{onlyNew ? "Added in the last 30 days" : "Shop the collection"}</span>
        <h1 data-testid="shop-title">{onlyNew ? `New In${category === "All" ? "" : ` · ${category}`}` : category === "All" ? "All pieces" : category}</h1>
        <p>{loading ? "Loading…" : `${filtered.length} piece${filtered.length === 1 ? "" : "s"}`} · Free shipping over ₹2,500</p>
      </header>

      <div className="shop-body">
        <aside className="shop-filters" data-testid="shop-filters">
          <div className="filter-group">
            <p className="filter-label">Category</p>
            <button className={onlyNew ? "active" : ""} onClick={() => setFilter("new", onlyNew ? "" : "1")} data-testid="filter-new-in">New In</button>
            {CATEGORIES.map(c => (
              <button key={c} className={category === c ? "active" : ""} onClick={() => setFilter("category", c === "All" ? "" : c)} data-testid={`filter-category-${c.toLowerCase().replace(/ /g, "-")}`}>{c}</button>
            ))}
          </div>
          <div className="filter-group">
            <p className="filter-label">Size</p>
            <div className="filter-sizes">
              {SIZES.map(s => <button key={s} className={size === s ? "active" : ""} onClick={() => setFilter("size", size === s ? "" : s)} data-testid={`filter-size-${s}`}>{s}</button>)}
            </div>
          </div>
          <div className="filter-group">
            <p className="filter-label">Max price</p>
            <input type="range" min="1000" max="5000" step="500" value={maxPrice || 5000} onChange={e => setFilter("max", e.target.value === "5000" ? "" : e.target.value)} data-testid="filter-price-range" />
            <span className="filter-range-value">{maxPrice ? `Under ${money(Number(maxPrice))}` : "Any price"}</span>
          </div>
          <div className="filter-group">
            <p className="filter-label">Sort by</p>
            <select value={sort} onChange={e => setFilter("sort", e.target.value === "featured" ? "" : e.target.value)} data-testid="filter-sort">
              <option value="featured">Featured</option>
              <option value="price-asc">Price · low to high</option>
              <option value="price-desc">Price · high to low</option>
              <option value="rating">Highest rated</option>
            </select>
          </div>
          <button className="link-btn" onClick={clearAll} data-testid="filter-clear">Clear all filters</button>
        </aside>

        <div className="shop-grid" data-testid="shop-grid">
          {loading ? <p className="shop-loading" data-testid="shop-loading">Loading the collection…</p>
            : filtered.length === 0
              ? <p className="shop-empty" data-testid="shop-empty">Nothing matches those filters yet. Try widening them.</p>
              : filtered.map(p => (
                <Link key={p.id} to={`/product/${p.id}`} className="lux-card" data-testid={`shop-card-${p.id}`}>
                  <div className="lux-card-image">
                    <img src={p.image} alt={p.name} loading="lazy" />
                    <NewBadge product={p} />
                    <span className="lux-card-hover">View piece →</span>
                  </div>
                  <div className="lux-card-info">
                    <p className="lux-card-cat">{p.category}</p>
                    <h3>{p.name}</h3>
                    <p className="lux-card-price">{money(p.price)} <del>{money(p.original_price)}</del></p>
                  </div>
                </Link>
              ))}
        </div>
      </div>
    </div>
  );
}
